import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRightIcon } from 'lucide-react';
import { Button } from '@monksflow/monks-ui';

import { EmptyState } from '@/shared/ui/EmptyState';
import {
  opportunitiesPreviewQuery,
  OpportunitiesCardSkeleton,
  isOpportunityNew,
} from '@/entities/opportunity';
import type {
  OpportunitiesPreviewDeps,
  OpportunitiesPreviewItem,
} from './OpportunitiesPreview.types';
import { useTranslation } from 'react-i18next';

export const OpportunitiesPreview = ({
  workspaceId,
  lastVisitedAt,
  OpportunitiesCard,
  onLaunchAll,
  onOpenItem,
}: OpportunitiesPreviewDeps) => {
  const { t } = useTranslation('springboard');

  const { data: opportunities = [], isLoading } = useQuery(opportunitiesPreviewQuery(workspaceId));

  // Show flash opportunities first; backfill with the rest sorted by recency
  // when there aren't enough flash to fill the three preview slots.
  const PREVIEW_LIMIT = 3;
  const flashOpps = opportunities.filter((opp) => opp.durationType?.value === 'flash');
  const otherOppsByRecency = [...opportunities]
    .filter((opp) => opp.durationType?.value !== 'flash')
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const top3 = [...flashOpps, ...otherOppsByRecency].slice(0, PREVIEW_LIMIT);

  const isNew = (opp: OpportunitiesPreviewItem) => isOpportunityNew(opp.createdAt, lastVisitedAt);

  // single call site, pure branch dispatch — inlined as a plain computed
  // value rather than a `renderCards`-style closure.
  let cards: ReactNode;
  if (isLoading) {
    cards = (
      <>
        {Array.from({ length: 3 }).map((_, i) => (
          <OpportunitiesCardSkeleton key={i} />
        ))}
      </>
    );
  } else if (top3.length > 0) {
    cards = (
      <>
        {top3.map((opp) => (
          <OpportunitiesCard
            key={opp.opportunityId}
            id={opp.opportunityId}
            item={opp}
            isNew={isNew(opp)}
            onClick={() => onOpenItem(opp)}
            onOpenDetail={() => onOpenItem(opp)}
          />
        ))}
      </>
    );
  } else {
    cards = (
      <EmptyState
        className="col-span-3"
        title={t('dashboard.opportunitiesPreview.noOpportunitiesYet')}
      />
    );
  }

  return (
    <section className="content-block gap-md rounded-xl" data-testid="home-opportunities">
      <div className="flex items-center justify-between">
        <div className="flex gap-2 flex-column flex-col items-start md:flex-row md:items-end">
          <h2 className="type-heading-sm leading-5 tracking-[-0.02em] text-foreground">
            {t('dashboard.opportunitiesPreview.opportunities')}
          </h2>
          <span className="text-sm text-foreground-alt hidden md:block">•</span>
          <span className="text-sm text-foreground-alt">
            {t('dashboard.opportunitiesPreview.rankedByShorterDuration')}
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={onLaunchAll}>
          {t('dashboard.opportunitiesPreview.allOpportunities')}
          <ArrowRightIcon className="size-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-grid md:grid-cols-2 lg:grid-cols-3">{cards}</div>
    </section>
  );
};
